/**
 * Return Lose Service
 * 
 * Service สำหรับจัดการระบบคืนยอดเสีย
 * เชื่อมต่อตาม Angular return-lose module
 */

import { queryClass, updateObject, createObject, callCloudFunction } from '@/services/adapters/parse/parse-client'
import type {
  ReturnLose,
  ReturnLoseLog,
  ReturnLoseQueryParams,
  ReturnLoseLogQueryParams,
  CreateReturnLoseData,
  ReturnLoseStats,
  ReturnLoseLogStats,
  ReturnLoseStatus,
} from './return-lose.types'

export class ReturnLoseService {
  /**
   * Map raw Parse data to ReturnLose interface
   */
  private static mapToReturnLose(raw: any): ReturnLose {
    const getDate = (value: any): Date | undefined => {
      if (!value) return undefined
      if (value instanceof Date) return value
      if (typeof value === 'string') return new Date(value)
      if (value.iso) return new Date(value.iso)
      return undefined
    }
    
    // Helper: map user pointer to object
    // รองรับทั้งกรณี expanded object และ Pointer
    const mapUser = (user: any) => {
      if (!user) return undefined
      // ถ้าเป็น Pointer ที่ยังไม่ expand (มีแค่ objectId)
      if (user.__type === 'Pointer') {
        return { objectId: user.objectId, firstname: '', lastname: '', username: '' }
      }
      // ถ้าเป็น expanded object
      return {
        objectId: user.objectId,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        username: user.username || '',
      }
    }

    return {
      id: raw.objectId,
      objectId: raw.objectId,
      uuid: raw.uuid || '',
      game_group: raw.game_group || [],
      start_date: getDate(raw.start_date)!,
      end_date: getDate(raw.end_date)!,
      date_return: raw.date_return,
      amount_lose: raw.amount_lose || 0,
      percent_return: raw.percent_return || 0,
      after_bonus: raw.after_bonus ?? true,
      return_bonus: raw.return_bonus ?? false,
      return_bonus_turnover: raw.return_bonus_turnover || 0,
      member_count: raw.member_count || 0,
      amount_count: raw.amount_count || 0,
      amount_after_bonus_count: raw.amount_after_bonus_count || 0,
      amount_return_count: raw.amount_return_count || 0,
      return_success_count: raw.return_success_count || 0,
      return_fail_count: raw.return_fail_count || 0,
      status: raw.status || 'Pending',
      user_created_id: mapUser(raw.user_created_id),
      user_updated_id: mapUser(raw.user_updated_id),
      createdAt: raw.createdAt || new Date().toISOString(),
      updatedAt: raw.updatedAt || new Date().toISOString(),
    }
  }

  /**
   * Map raw Parse data to ReturnLoseLog interface
   */
  private static mapToReturnLoseLog(raw: any): ReturnLoseLog {
    return {
      id: raw.objectId,
      objectId: raw.objectId,
      uuid: raw.uuid || '',
      return_lose_obj: raw.return_lose_obj ? {
        objectId: raw.return_lose_obj.objectId,
        game_group: raw.return_lose_obj.game_group || [],
        date_return: raw.return_lose_obj.date_return,
        amount_lose: raw.return_lose_obj.amount_lose || 0,
        percent_return: raw.return_lose_obj.percent_return || 0,
      } : undefined,
      member_id: raw.member_id ? {
        objectId: raw.member_id.objectId,
        agent_username: raw.member_id.agent_username,
        firstname: raw.member_id.firstname,
        lastname: raw.member_id.lastname,
        username: raw.member_id.username,
      } : undefined,
      amount_count: raw.amount_count || 0,
      amount_bonus: raw.amount_bonus || 0,
      amount_after_bonus_count: raw.amount_after_bonus_count || 0,
      amount_return_count: raw.amount_return_count || 0,
      return_to_member: raw.return_to_member || false,
      return_status: raw.return_status ?? 0,
      user_created_id: raw.user_created_id ? {
        objectId: raw.user_created_id.objectId,
        firstname: raw.user_created_id.firstname,
        lastname: raw.user_created_id.lastname,
        username: raw.user_created_id.username,
      } : undefined,
      createdAt: raw.createdAt || new Date().toISOString(),
      updatedAt: raw.updatedAt || new Date().toISOString(),
    }
  }

  // ============================================
  // ReturnLose Methods
  // ============================================

  /**
   * Find ReturnLose records with pagination
   * ใช้ REST API + fetch user data แยก
   */
  static async findReturnLose(
    sessionToken: string,
    params: ReturnLoseQueryParams = {}
  ): Promise<{ items: ReturnLose[]; total: number }> {
    const where: Record<string, any> = {
      deletedAt: { $exists: false }
    }

    if (params.status) {
      where.status = params.status
    }

    if (params.query) {
      where.$or = [
        { uuid: { $regex: params.query, $options: 'i' } },
        { canonical: { $regex: params.query.toLowerCase(), $options: 'i' } }
      ]
    }

    const queryOptions: any = {
      where,
      limit: params.limit || 20,
      skip: params.page ? (params.page - 1) * (params.limit || 20) : 0,
      order: '-createdAt',
      // บังคับให้ return user_created_id และ user_updated_id fields
      keys: [
        'objectId', 'uuid', 'game_group', 'date_return', 'start_date', 'end_date',
        'amount_lose', 'percent_return', 'after_bonus', 'return_bonus', 'return_bonus_turnover',
        'member_count', 'amount_count', 'amount_return_count', 'amount_after_bonus_count',
        'return_success_count', 'return_fail_count', 'status',
        'user_created_id', 'user_updated_id', 'createdAt', 'updatedAt'
      ],
    }

    const result = await queryClass('ReturnLose', queryOptions, sessionToken)
    
    console.log('🔍 [ReturnLose] Raw results count:', result.results?.length)
    
    // Collect user IDs from raw data
    const userIds = new Set<string>()
    for (const raw of result.results || []) {
      if (raw.user_created_id?.objectId) {
        userIds.add(raw.user_created_id.objectId)
      }
      if (raw.user_updated_id?.objectId) {
        userIds.add(raw.user_updated_id.objectId)
      }
    }
    
    console.log('🔍 [ReturnLose] User IDs found:', userIds.size, Array.from(userIds))
    
    // Fetch user data
    const userMap = new Map<string, { firstname: string; lastname: string; username: string }>()
    if (userIds.size > 0) {
      try {
        const usersResult = await queryClass('_User', {
          where: { objectId: { $in: Array.from(userIds) } },
          keys: ['firstname', 'lastname', 'username'],
          limit: userIds.size,
        }, sessionToken)
        
        console.log('🔍 [ReturnLose] Users fetched:', usersResult.results?.length)
        
        for (const user of usersResult.results || []) {
          userMap.set(user.objectId, {
            firstname: user.firstname || '',
            lastname: user.lastname || '',
            username: user.username || '',
          })
        }
        
        console.log('🔍 [ReturnLose] User map:', Object.fromEntries(userMap))
      } catch (userError) {
        console.warn('⚠️ [ReturnLose] Failed to fetch user data:', userError)
      }
    }
    
    // Map items พร้อม inject user data
    const items = (result.results || []).map((raw: any) => {
      const item = this.mapToReturnLose(raw)
      
      // Inject user_created_id
      if (raw.user_created_id?.objectId) {
        const userData = userMap.get(raw.user_created_id.objectId)
        console.log('🔍 [ReturnLose] Injecting user for', raw.objectId, ':', userData)
        if (userData) {
          item.user_created_id = { objectId: raw.user_created_id.objectId, ...userData }
        }
      }
      
      // Inject user_updated_id
      if (raw.user_updated_id?.objectId) {
        const userData = userMap.get(raw.user_updated_id.objectId)
        if (userData) {
          item.user_updated_id = { objectId: raw.user_updated_id.objectId, ...userData }
        }
      }
      
      return item
    })

    // Debug: ตรวจสอบ items ก่อน return
    const withUser = items.filter(i => i.user_created_id?.firstname)
    console.log(`✅ [ReturnLose] ${withUser.length}/${items.length} items have user firstname`)
    if (items[0]) {
      console.log('✅ [ReturnLose] First item user_created_id:', JSON.stringify(items[0].user_created_id))
    }

    return {
      items,
      total: result.count || 0,
    }
  }

  /**
   * Get ReturnLose by ID
   */
  static async getReturnLoseById(
    sessionToken: string,
    id: string
  ): Promise<ReturnLose | null> {
    const result = await queryClass(
      'ReturnLose',
      {
        where: { objectId: id },
        limit: 1,
        include: ['user_created_id', 'user_updated_id'],
      },
      sessionToken
    )

    if (result.results && result.results.length > 0) {
      return this.mapToReturnLose(result.results[0])
    }

    return null
  }

  /**
   * Get ReturnLose stats
   * คำนวณเฉพาะรายการที่ไม่ได้ลบ (deletedAt: { $exists: false })
   */
  static async getReturnLoseStats(sessionToken: string): Promise<ReturnLoseStats> {
    // ต้องเพิ่ม deletedAt: { $exists: false } ทุก query
    const baseWhere = { deletedAt: { $exists: false } }
    
    const [total, pending, approved, rejected] = await Promise.all([
      queryClass('ReturnLose', { where: { ...baseWhere }, limit: 0 }, sessionToken),
      queryClass('ReturnLose', { where: { ...baseWhere, status: 'Pending' }, limit: 0 }, sessionToken),
      queryClass('ReturnLose', { where: { ...baseWhere, status: 'Approved' }, limit: 0 }, sessionToken),
      queryClass('ReturnLose', { where: { ...baseWhere, status: 'Rejected' }, limit: 0 }, sessionToken),
    ])

    // Get total returned amount (เฉพาะที่ Approved และยังไม่ได้ลบ)
    const approvedItems = await queryClass(
      'ReturnLose',
      { where: { ...baseWhere, status: 'Approved' }, limit: 1000 },
      sessionToken
    )
    
    const totalReturned = (approvedItems.results || []).reduce(
      (sum: number, item: any) => sum + (item.amount_return_count || 0),
      0
    )

    return {
      total: total.count || 0,
      pending: pending.count || 0,
      approved: approved.count || 0,
      rejected: rejected.count || 0,
      totalReturned,
    }
  }

  /**
   * Create new ReturnLose
   * ตาม Angular: ใช้ Parse.Object.save() ตรงๆ
   */
  static async createReturnLose(
    sessionToken: string,
    data: CreateReturnLoseData
  ): Promise<ReturnLose> {
    // Format dates as DD-MM-YYYY string (Angular style)
    const formatDate = (d: Date): string => {
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const year = d.getFullYear()
      return `${day}-${month}-${year}`
    }
    
    const startStr = formatDate(data.start_date)
    const endStr = formatDate(data.end_date)

    // Angular saves dates as formatted strings, not Parse Date objects
    const saveData: Record<string, any> = {
      game_group: data.game_group,
      start_date: startStr,  // Angular: formData.start_date.format('DD-MM-YYYY')
      end_date: endStr,      // Angular: formData.end_date.format('DD-MM-YYYY')
      amount_lose: data.amount_lose,
      percent_return: data.percent_return,
      after_bonus: data.after_bonus ?? true,
      return_bonus: data.return_bonus ?? false,
      return_bonus_turnover: data.return_bonus_turnover ?? 0,
      status: 'Pending',
      return_success_count: 0,
      return_fail_count: 0,
    }

    try {
      console.log('📤 Creating ReturnLose:', saveData)
      const result = await createObject('ReturnLose', saveData)
      
      // Fetch the full object with includes
      const fullObj = await this.getReturnLoseById(sessionToken, result.objectId)
      return fullObj || this.mapToReturnLose({ ...saveData, objectId: result.objectId, createdAt: result.createdAt })
    } catch (error) {
      console.error('Error creating ReturnLose:', error)
      throw error
    }
  }

  /**
   * Update existing ReturnLose
   * ตาม Angular: ใช้ Parse.Object.save() ตรงๆ
   */
  static async updateReturnLose(
    sessionToken: string,
    id: string,
    data: Partial<CreateReturnLoseData>
  ): Promise<ReturnLose> {
    const updateData: Record<string, any> = {}

    if (data.game_group) updateData.game_group = data.game_group
    if (data.amount_lose !== undefined) updateData.amount_lose = data.amount_lose
    if (data.percent_return !== undefined) updateData.percent_return = data.percent_return
    if (data.after_bonus !== undefined) updateData.after_bonus = data.after_bonus
    if (data.return_bonus !== undefined) updateData.return_bonus = data.return_bonus
    if (data.return_bonus_turnover !== undefined) updateData.return_bonus_turnover = data.return_bonus_turnover

    if (data.start_date) {
      const d = data.start_date
      updateData.start_date = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
    }
    if (data.end_date) {
      const d = data.end_date
      updateData.end_date = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
    }

    try {
      await updateObject('ReturnLose', id, updateData)
      const fullObj = await this.getReturnLoseById(sessionToken, id)
      return fullObj!
    } catch (error) {
      console.error('Error updating ReturnLose:', error)
      throw error
    }
  }

  /**
   * Update ReturnLose status
   */
  static async updateReturnLoseStatus(
    _sessionToken: string,
    id: string,
    status: ReturnLoseStatus
  ): Promise<void> {
    await updateObject('ReturnLose', id, { status })
  }

  /**
   * Delete ReturnLose
   */
  static async deleteReturnLose(
    _sessionToken: string,
    id: string
  ): Promise<void> {
    // Soft delete or actual delete
    await updateObject('ReturnLose', id, { 
      status: 'Rejected',
      deletedAt: { __type: 'Date', iso: new Date().toISOString() }
    })
  }

  // ============================================
  // ReturnLoseLog Methods
  // ============================================

  /**
   * Find ReturnLoseLog records by UUID
   */
  static async findReturnLoseLogs(
    sessionToken: string,
    params: ReturnLoseLogQueryParams
  ): Promise<{ items: ReturnLoseLog[]; total: number }> {
    const where: Record<string, any> = {
      uuid: params.uuid,
    }

    if (params.return_status !== undefined) {
      where.return_status = params.return_status
    }

    if (params.query) {
      // Search by member username
      where['member_id.agent_username'] = { $regex: params.query, $options: 'i' }
    }

    const queryOptions: any = {
      where,
      limit: params.limit || 100,
      skip: params.page ? (params.page - 1) * (params.limit || 100) : 0,
      order: '-createdAt',
      include: ['member_id', 'return_lose_obj', 'user_created_id'],
    }

    const result = await queryClass('ReturnLoseLog', queryOptions, sessionToken)

    return {
      items: (result.results || []).map(this.mapToReturnLoseLog),
      total: result.count || 0,
    }
  }

  /**
   * Get ReturnLoseLog stats by UUID
   */
  static async getReturnLoseLogStats(
    sessionToken: string,
    uuid: string
  ): Promise<ReturnLoseLogStats> {
    const [total, waiting, success, failed] = await Promise.all([
      queryClass('ReturnLoseLog', { where: { uuid }, limit: 0 }, sessionToken),
      queryClass('ReturnLoseLog', { where: { uuid, return_status: 0 }, limit: 0 }, sessionToken),
      queryClass('ReturnLoseLog', { where: { uuid, return_status: 1 }, limit: 0 }, sessionToken),
      queryClass('ReturnLoseLog', { where: { uuid, return_status: 2 }, limit: 0 }, sessionToken),
    ])

    // Get total amount
    const allLogs = await queryClass(
      'ReturnLoseLog',
      { where: { uuid, return_to_member: true }, limit: 1000 },
      sessionToken
    )
    
    const totalAmount = (allLogs.results || []).reduce(
      (sum: number, item: any) => sum + (item.amount_return_count || 0),
      0
    )

    return {
      total: total.count || 0,
      waiting: waiting.count || 0,
      success: success.count || 0,
      failed: failed.count || 0,
      totalAmount,
    }
  }

  /**
   * Toggle return_to_member for a log
   * ตาม Angular: setAllowReturnToMember(id, status)
   */
  static async setAllowReturnToMember(
    _sessionToken: string,
    logId: string,
    status: boolean
  ): Promise<boolean> {
    try {
      console.log('📤 Calling setAllowReturnToMember:', { id: logId, status })
      const result = await callCloudFunction('setAllowReturnToMember', { id: logId, status })
      console.log('✅ setAllowReturnToMember result:', result)
      return result
    } catch (error) {
      console.error('Error calling setAllowReturnToMember:', error)
      throw error
    }
  }

  /**
   * Process return for all selected members
   * ตาม Angular: setReturnToMember(uuid)
   * - เรียกด้วย uuid ของ ReturnLose
   * - จะคืนยอดให้สมาชิกทุกคนที่มี return_to_member = true
   */
  static async setReturnToMember(
    _sessionToken: string,
    uuid: string
  ): Promise<boolean> {
    try {
      console.log('📤 Calling setReturnToMember:', { id: uuid })
      const result = await callCloudFunction('setReturnToMember', { id: uuid })
      console.log('✅ setReturnToMember result:', result)
      return result
    } catch (error) {
      console.error('Error calling setReturnToMember:', error)
      throw error
    }
  }
}

